import { FormGroup, Subtext } from '@components/async-form';
import { AutosizeText } from '@components/autosize-text';
import { Button } from '@components/buttons';
import { IcoUsers } from '@components/icons';
import { useState } from 'preact/hooks';
import { BULK_INSERT_LIMIT } from 'shared/consts';
import { pluralize } from 'shared/formatting';
import { extractEmails } from 'shared/parsing';
import quikpik from 'quikpik';
import { Toggle } from '@components/toggle';
import { RpxResponse, rpx } from 'client/lib/rpx-client';
import { showProcessingModal } from '@course/components/processing-modal';
import { ModalForm } from '@components/modal-form';
import { Course } from 'server/types';

type AddStudentsInBulkResult = RpxResponse<typeof rpx.admin.bulkAddStudentsToCourse>;

interface Props {
  course: Pick<Course, 'id' | 'isBundle'>;
  onCancel(): void;
  onSent(result: AddStudentsInBulkResult): void;
}

function csvPicker(onPick: (content: string) => void) {
  quikpik({
    sources: ['filepicker'],
    accept: 'text/*',
    customProgress: true,
    upload({ files }) {
      const file = files[0];
      const reader = new FileReader();
      reader.addEventListener('load', () => onPick(reader.result as string), false);
      reader.readAsText(file);
    },
  });
}

export function BulkEnrollStudentsModal({ course, onSent, onCancel }: Props) {
  const [text, setText] = useState('');
  const [sendWelcomeEmails, setShouldSendWelcomeEmails] = useState(false);
  const numEmails = text.split('@').length - 1;
  const courseLabel = course.isBundle ? 'bundle' : 'course';

  async function submit() {
    const emails = extractEmails(text);
    const result = await showProcessingModal({
      promise: rpx.admin.bulkAddStudentsToCourse({
        courseId: course.id,
        emails,
        sendWelcomeEmails,
      }),
      title: `Adding ${numEmails} ${pluralize('student', numEmails)} to the ${courseLabel}`,
      children: (
        <div class="scale-50">
          <div class="flex items-center -rotate-12">
            <span class="flex flex-col items-end mr-3 absolute right-full">
              <span class="bg-indigo-200 inline-block rounded-lg ml-3 h-2 w-16"></span>
              <span class="bg-gray-200 inline-block rounded-lg ml-3 h-2 w-28 mt-2"></span>
            </span>
            <span class="w-52 h-52 flex items-center justify-center text-indigo-300">
              <IcoUsers class="w-32 h-32" />
            </span>
          </div>
        </div>
      ),
    });
    if (result) {
      onSent(result);
    }
  }

  return (
    <ModalForm
      onClose={onCancel}
      onSubmit={submit}
      title="Add students in bulk"
      subtitle={
        <>
          Add up to {BULK_INSERT_LIMIT} email addresses one per line. You can also{' '}
          <Button class="text-indigo-600" onClick={() => csvPicker(setText)}>
            load a CSV file
          </Button>
          .
        </>
      }
      confirmButtonText={
        <>
          Add {numEmails || ''} {pluralize('student', numEmails)}
        </>
      }
    >
      <Subtext>
        This will add students directly to the {courseLabel} if they have an account. If they don't,
        an invitation will be sent.
      </Subtext>
      <div
        class="overflow-y-scroll max-h-60 border border-gray-300 rounded-sm focus-within:ring-indigo-400 focus-within:border-indigo-400 focus-within:ring-1"
        data-private
      >
        <FormGroup prop="emails" class="w-full">
          <AutosizeText
            focusSelf
            name="emails"
            class="min-h-32 border-none"
            value={text}
            onInput={(e: any) => setText(e.target.value)}
            placeholder="Emails, separated by space, comma, or newlines"
          />
        </FormGroup>
      </div>
      <footer class="mt-4 space-x-3">
        <label class="flex gap-2 cursor-pointer mt-6">
          <Toggle
            checked={sendWelcomeEmails}
            onClick={() => setShouldSendWelcomeEmails(!sendWelcomeEmails)}
          />
          <span>Send welcome to the {courseLabel} emails</span>
        </label>
        {sendWelcomeEmails && (
          <p class="pl-10 text-xs">
            You may visit the {courseLabel} messages page to modify the {courseLabel} welcome email
            template.
          </p>
        )}
        {!!numEmails && numEmails > BULK_INSERT_LIMIT && (
          <p class="text-red-600 text-xs text-right">
            You can only add up to {BULK_INSERT_LIMIT} students.
          </p>
        )}
      </footer>
    </ModalForm>
  );
}
